import type { VirtualListStateSnapshot } from "@tsmono/react/virtual";
import { clearDocumentSelection, getOwn } from "@tsmono/util";

import { AppState } from "../app/types";
import { Capabilities } from "../client/api/types";
import { kLogViewSamplesTabId, kSampleTranscriptTabId } from "../constants";

import { StoreState } from "./store";

export interface AppSlice {
  app: AppState;
  capabilities: Capabilities;
  appActions: {
    setShowFind: (show: boolean) => void;
    hideFind: () => void;
    setNativeFind: (nativeFind: boolean) => void;

    setShowingTranscriptFilterDialog: (showing: boolean) => void;
    setShowingOptionsDialog: (showing: boolean) => void;
    setWorkspaceTab: (tab: string) => void;
    clearWorkspaceTab: () => void;

    setSampleTab: (tab: string) => void;
    clearSampleTab: () => void;

    getScrollPosition: (name: string) => number | undefined;
    setScrollPosition: (name: string, value: number) => void;

    getListPosition: (name: string) => VirtualListStateSnapshot | undefined;
    setListPosition: (name: string, state: VirtualListStateSnapshot) => void;
    clearListPosition: (name: string) => void;

    getVisibleRange: (name: string) => {
      startIndex: number;
      endIndex: number;
      totalCount: number;
    };
    setVisibleRange: (
      name: string,
      value: { startIndex: number; endIndex: number; totalCount: number }
    ) => void;
    clearVisibleRange: (name: string) => void;

    getCollapsed: (name: string, defaultValue?: boolean) => boolean;
    setCollapsed: (name: string, value: boolean) => void;

    getMessageVisible: (name: string, defaultValue?: boolean) => boolean;
    setMessageVisible: (name: string, value: boolean) => void;
    clearMessageVisible: (name: string) => void;

    getPropertyValue: <T>(bagName: string, key: string, defaultValue?: T) => T;
    setPropertyValue: <T>(bagName: string, key: string, value: T) => void;
    removePropertyValue: (bagName: string, key: string) => void;
    removeAllProperties: (bagName: string) => void;
    removeBagsByPrefix: (bagNamePrefix: string) => void;
    removeByPrefix: (bagName: string, prefix: string) => void;

    setDisplayMode: (mode: "raw" | "rendered") => void;

    setLogsSampleView: (logsSampleView: boolean) => void;
  };
}

const kDefaultWorkspaceTab = kLogViewSamplesTabId;
const kDefaultSampleTab = kSampleTranscriptTabId;

const initialState: AppState = {
  showFind: false,
  dialogs: {
    transcriptFilter: false,
    options: false,
  },
  tabs: {
    workspace: kDefaultWorkspaceTab,
    sample: kDefaultSampleTab,
  },
  scrollPositions: {},
  listPositions: {},
  visibleRanges: {},
  collapsed: {},
  messages: {},
  propertyBags: {},
  displayMode: "rendered",
  logsSampleView: false,
};

export const createAppSlice = (
  set: (fn: (state: StoreState) => void) => void,
  get: () => StoreState,
  _store: unknown
): AppSlice => {
  const getBoolRecord = (
    record: Record<string, boolean>,
    name: string,
    defaultValue?: boolean
  ) => {
    if (Object.hasOwn(record, name)) {
      return record[name] ?? false;
    } else {
      return defaultValue || false;
    }
  };

  const slice = {
    // State
    app: initialState,
    // Replaced by initializeStore; nothing is available until it runs.
    capabilities: {
      downloadFiles: false,
      downloadLogs: false,
      webWorkers: false,
      streamSamples: false,
    },

    // Actions
    appActions: {
      setShowFind: (show: boolean) =>
        set((state) => {
          state.app.showFind = show;
        }),

      hideFind: () => {
        clearDocumentSelection();
        set((state) => {
          state.app.showFind = false;
        });
      },
      setNativeFind: (nativeFind: boolean) =>
        set((state) => {
          state.app.nativeFind = nativeFind;
        }),
      setShowingTranscriptFilterDialog: (showing: boolean) => {
        const state = get();
        const isShowing = state.app.dialogs.transcriptFilter;
        if (showing === isShowing) {
          return;
        }
        set((state) => {
          state.app.dialogs.transcriptFilter = showing;
        });
      },
      setShowingOptionsDialog: (showing: boolean) => {
        const state = get();
        const isShowing = state.app.dialogs.options;
        if (showing === isShowing) {
          return;
        }
        set((state) => {
          state.app.dialogs.options = showing;
        });
      },
      setWorkspaceTab: (tab: string) => {
        set((state) => {
          state.app.tabs.workspace = tab;
        });
      },
      clearWorkspaceTab: () => {
        set((state) => {
          state.app.tabs.workspace = kDefaultWorkspaceTab;
        });
      },
      setSampleTab: (tab: string) => {
        set((state) => {
          state.app.tabs.sample = tab;
        });
      },
      clearSampleTab: () => {
        set((state) => {
          state.app.tabs.sample = kDefaultSampleTab;
        });
      },
      getScrollPosition: (name: string) => {
        const state = get();
        return state.app.scrollPositions[name];
      },
      setScrollPosition: (name: string, position: number) => {
        set((state) => {
          state.app.scrollPositions[name] = position;
        });
      },
      getListPosition: (name: string) => {
        const state = get();
        if (Object.keys(state.app.listPositions).includes(name)) {
          return state.app.listPositions[name];
        } else {
          return undefined;
        }
      },
      setListPosition: (name: string, position: VirtualListStateSnapshot) => {
        set((state) => {
          state.app.listPositions[name] = position;
        });
      },
      clearListPosition: (name: string) => {
        set((state) => {
          // Remove the key
          const newListPositions = { ...state.app.listPositions };
          delete newListPositions[name];

          return {
            app: {
              ...state.app,
              listPositions: newListPositions,
            },
          };
        });
      },
      getVisibleRange: (name: string) => {
        const state = get();
        if (Object.hasOwn(state.app.visibleRanges, name)) {
          return (
            state.app.visibleRanges[name] ?? {
              startIndex: 0,
              endIndex: 0,
              totalCount: 0,
            }
          );
        } else {
          return { startIndex: 0, endIndex: 0, totalCount: 0 };
        }
      },
      setVisibleRange: (
        name: string,
        value: { startIndex: number; endIndex: number; totalCount: number }
      ) => {
        set((state) => {
          state.app.visibleRanges[name] = value;
        });
      },
      clearVisibleRange: (name: string) => {
        set((state) => {
          // Remove the key
          const newVisibleRanges = { ...state.app.visibleRanges };
          delete newVisibleRanges[name];

          return {
            app: {
              ...state.app,
              visibleRanges: newVisibleRanges,
            },
          };
        });
      },
      getCollapsed: (name: string, defaultValue?: boolean) => {
        return getBoolRecord(get().app.collapsed, name, defaultValue);
      },
      setCollapsed: (name: string, value: boolean) => {
        set((state) => {
          state.app.collapsed[name] = value;
        });
      },
      getMessageVisible: (name: string, defaultValue?: boolean) => {
        return getBoolRecord(get().app.messages, name, defaultValue);
      },
      setMessageVisible: (name: string, value: boolean) => {
        set((state) => {
          state.app.messages[name] = value;
        });
      },
      clearMessageVisible: (name: string) => {
        set((state) => {
          delete state.app.messages[name];
        });
      },
      getPropertyValue: <T>(
        bagName: string,
        key: string,
        defaultValue?: T
      ): T => {
        const bag = getOwn(get().app.propertyBags, bagName);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- unsound-by-design generic accessor: property bags hold unknown, and T is the caller's claim about their own property
        return (
          bag !== undefined && Object.hasOwn(bag, key) ? bag[key] : defaultValue
        ) as T;
      },

      setPropertyValue: <T>(bagName: string, key: string, value: T) => {
        set((state) => {
          const bags = state.app.propertyBags;
          const bag = getOwn(bags, bagName);
          if (bag !== undefined && key !== "__proto__") {
            bag[key] = value;
            return;
          }
          // Computed keys bypass the inherited __proto__ setter, which
          // Immer rejects even when the draft already owns that property.
          const next = { ...bag, [key]: value };
          if (bagName === "__proto__") {
            state.app.propertyBags = { ...bags, [bagName]: next };
          } else {
            bags[bagName] = next;
          }
        });
      },

      removePropertyValue: (bagName: string, key: string) => {
        set((state) => {
          const bag = getOwn(state.app.propertyBags, bagName);
          if (bag !== undefined) {
            delete bag[key];
          }
        });
      },

      removeAllProperties: (bagName: string) => {
        set((state) => {
          delete state.app.propertyBags[bagName];
        });
      },

      // Remove every bag whose name starts with the prefix. Used to drop a
      // sample's scroll snapshots on exit, where the bag name carries a dynamic
      // suffix (e.g. the transcript's `:<timeline>` selection).
      removeBagsByPrefix: (bagNamePrefix: string) => {
        set((state) => {
          for (const name of Object.keys(state.app.propertyBags)) {
            if (name.startsWith(bagNamePrefix)) {
              delete state.app.propertyBags[name];
            }
          }
        });
      },

      removeByPrefix: (bagName: string, prefix: string) => {
        set((state) => {
          const bag = getOwn(state.app.propertyBags, bagName);
          if (!bag) return;
          let changed = false;
          for (const key of Object.keys(bag)) {
            if (key.startsWith(prefix)) {
              delete bag[key];
              changed = true;
            }
          }
          if (changed && Object.keys(bag).length === 0) {
            delete state.app.propertyBags[bagName];
          }
        });
      },

      setDisplayMode: (mode: "raw" | "rendered") => {
        set((state) => {
          state.app.displayMode = mode;
        });
      },
      setLogsSampleView: (logsSampleView: boolean) => {
        set((state) => {
          state.app.logsSampleView = logsSampleView;
        });
      },
    },
  } as const;

  return slice;
};

export const initializeAppSlice = (
  set: (fn: (state: StoreState) => void) => void,
  capabilities: Capabilities
) => {
  set((state) => {
    state.capabilities = capabilities;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!state.app) {
      state.app = initialState;
    }
  });
};
